import * as React from "react"

import { AlertTriangleIcon } from "lucide-react"

import { useTranslations } from "@/i18n"
import { useWidgetMenu } from "@ibada/dashboard-engine"

import { WidgetBoundary } from "@/features/finance/overview/components/widget-boundary"
import { formatMoneySafe, formatPercent } from "@/features/finance/overview/lib/overview-format"
import { OverviewRoutes, useOverviewNav } from "@/features/finance/overview/lib/overview-nav"
import {
  PerformanceRowsSkeleton,
  useBudgetPerformance,
} from "@/features/finance/overview/widgets/budget-performance-shared"
import type { BudgetPerformanceLine } from "@/features/budgeting/model/budget-performance-types"

const MAX_ROWS = 6

/**
 * Overspend & Variance — expense budget lines at or over 100% of plan, worst first.
 *
 * The server decides what belongs here, and it lists EXPENSE lines only. An income line at 120% has beaten
 * its target; alerting on it would tell a treasurer that receiving more than planned is a problem.
 *
 * An empty list is the good state, so it says so rather than showing a bare "no data".
 */
export function BudgetOverspendWidget() {
  const t = useTranslations("finance.overview")
  const go = useOverviewNav()
  const query = useBudgetPerformance()
  const currency = query.data?.currency ?? ""

  const rows = React.useMemo<BudgetPerformanceLine[] | undefined>(
    () => (query.data ? query.data.overspent.slice(0, MAX_ROWS) : undefined),
    [query.data],
  )

  useWidgetMenu({
    refresh: () => void query.refetch(),
    viewDetails: go.canNavigateTo(OverviewRoutes.budgets) ? () => go(OverviewRoutes.budgets) : undefined,
  })

  return (
    <WidgetBoundary
      isLoading={query.isLoading}
      isError={query.isError}
      isFetching={query.isFetching}
      data={rows}
      isEmpty={(list) => list.length === 0}
      skeleton={<PerformanceRowsSkeleton />}
      errorTitle={t("widgets.budgetOverspend.error")}
      onRetry={() => void query.refetch()}
      retryLabel={t("widgets.common.retry")}
      emptyTitle={t("widgets.budgetOverspend.empty.title")}
    >
      {(list) => (
        <ul className="divide-y divide-border/60">
          {list.map((line) => (
            <li key={line.budgetLineId} className="flex items-center gap-2 py-2 first:pt-0 last:pb-0">
              <AlertTriangleIcon className="size-3.5 shrink-0 text-destructive" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">
                  {line.accountName ?? line.lineTitle}
                </p>
                <p className="truncate text-xs tabular-nums text-muted-foreground">
                  {formatMoneySafe(line.actual, currency)} / {formatMoneySafe(line.planned, currency)}
                  {line.fundName ? ` · ${line.fundName}` : ""}
                </p>
              </div>
              <span className="shrink-0 text-xs font-semibold tabular-nums text-destructive">
                {line.utilizationPct === null ? "—" : formatPercent(line.utilizationPct)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </WidgetBoundary>
  )
}
